<template>
  <div class="mb-10 min-w-0 mt-4 relative">
    <div
      v-if="swiperInstance"
      class="absolute top-1/2 flex justify-between w-full h-full -translate-y-1/2"
    >
      <button
        v-if="swiperInstance.allowSlidePrev"
        :class="buttonRing"
        @click="swiperInstance.slidePrev()"
      >
        <IconChevronLeft class="size-4 -translate-x-px" />
      </button>
      <button
        v-if="swiperInstance.allowSlideNext"
        :class="buttonRing"
        @click="swiperInstance.slideNext()"
      >
        <IconChevronLeft class="size-4 rotate-180 translate-x-px" />
      </button>
    </div>

    <!-- Calendar-->
    <div
      :class="{
        'mx-8':
          swiperInstance &&
          (swiperInstance?.allowSlidePrev || swiperInstance?.allowSlideNext),
      }"
    >
      <Swiper
        v-if="currentIndex !== null"
        v-bind="swiperOptions"
        @swiper="setSwiperInstance"
      >
        <template v-for="(d, i) in dates">
          <SwiperSlide
            v-if="!dates[i - 1] || dates[i - 1].month !== d.month"
            class="!w-[20px] !h-[57px]"
          >
            <div
              class="flex h-full items-center bg-black-50 dark:bg-black-50/10 rounded-md"
            >
              <span
                class="block uppercase text-black-800 dark:text-black-200 text-[10px] w-full pl-0.5 text-center [writing-mode:vertical-lr] rotate-180"
                v-text="months[d.month]"
              />
            </div>
          </SwiperSlide>
          <SwiperSlide class="!w-[50px]">
            <div
              class="flex"
              tabindex="-1"
              :aria-label="d.label"
              :title="d.label"
            >
              <button
                class="flex flex-col hover:border-primary hover:dark:border-brand-1 rounded-md py-1.5 w-[50px] flex-none group border transition-colors group"
                :class="[
                  d.value === value
                    ? 'border-primary dark:border-brand-1 dark:text-white today'
                    : 'border-transparent',
                  {
                    'pointer-events-none opacity-50 grayscale': d.disabled,
                  },
                ]"
                @click="setDate(d)"
              >
                <span
                  class="block font-semibold text-lg w-full"
                  v-text="d.date"
                />
                <span
                  class="block uppercase group-[.today]:dark:text-white text-[10px] w-full"
                  v-text="d.day"
                  :class="
                    d.weekend
                      ? 'text-rose-700 dark:text-rose-500'
                      : 'text-black-700 dark:text-black-300'
                  "
                />
              </button>
            </div>
          </SwiperSlide>
        </template>
      </Swiper>
    </div>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { IconChevronLeft } from '@tabler/icons-vue'

const emits = defineEmits(['update:model-value'])

dayjs.extend(customParseFormat)

const props = defineProps({
  modelValue: {
    type: [String, Date],
    default: null,
  },
  dates: {
    type: Array,
    default: [],
  },
})

const buttonRing =
  'ring-1 ring-primary text-primary dark:text-brand-1 ring-inset dark:ring-brand-1 px-1 rounded-md h-full transform transition-all hover:-translate-x-0.5 ease-in-out disabled:ring-black-200 disabled:text-black-200'

const value = computed({
  get: () => props.modelValue,
  set: (val) => emits('update:model-value', val),
})

const days = ref(['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'])
const months = ref([
  'янв',
  'фев',
  'март',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сент',
  'окт',
  'нояб',
  'дек',
])

const dates = ref([])

const currentPage = ref(0)
const totalPages = ref(0)

const currentIndex = ref(null)

const swiperInstance = ref(null)
const swiperOptions = ref({
  initialSlide: currentIndex.value,
  slidesPerView: 'auto',
  spaceBetween: 8,
  breakpoints: {
    240: {
      slidesPerGroupAuto: true,
    },
    640: {
      slidesPerGroup: 7,
      slidesPerGroupAuto: false,
    },
  },
})

function setSwiperInstance(instance) {
  swiperInstance.value = instance
}

watch(
  () => props.modelValue,
  () => {
    if (!value.value) {
      value.value = dayjs().format('DD.MM.YYYY')
    } else if (
      typeof value.value === 'string' &&
      !dayjs(value.value, 'DD.MM.YYYY').isValid() &&
      dayjs(value.value).isValid()
    ) {
      // We are working with ISO format.
      value.value = dayjs(value.value).format('DD.MM.YYYY')
    }
  },
  {
    immediate: true,
  },
)

function setDate(d) {
  value.value = d.value
}

watch(
  () => value.value,
  () => {
    if (value.value) {
      let d
      let today = dayjs()
      if (typeof value.value === 'string') {
        d = dayjs(value.value, 'DD.MM.YYYY').locale('ru')
      } else {
        d = dayjs(value.value).locale('ru')
      }

      const datesCount = 180

      dates.value = Array.from(Array(datesCount).keys())
        .map((i, index) => {
          const n = today.add(i, 'days')
          if (props.dates.includes(n.format('DD.MM.YYYY'))) {
            return {
              month: n.month(),
              label: n.format('D MMM YYYY'),
              date: n.date(),
              disabled: n.isBefore(today.startOf('day')),
              day: days.value[n.day()],
              today: n.isSame(d.startOf('day')),
              weekend: n.day() === 0 || n.day() === 6,
              value: n.format('DD.MM.YYYY'),
            }
          }
        })
        .filter(Boolean)

      currentIndex.value = 0
      swiperOptions.value.initialSlide = 0

      currentPage.value = Math.floor(
        Math.abs(today.startOf('month').diff(d, 'days')) / 6,
      )

      totalPages.value = Math.ceil(dates.value.length / 6)
    }
  },
  {
    immediate: true,
  },
)

onMounted(() => {
  nextTick(() => {
    setDate({
      value: props.dates[0],
    })
  })
})
</script>
